'use client';

import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

let rumInitialized = false;
const ERROR_RATE_LIMIT_WINDOW_MS = 60 * 1000;
const ERROR_RATE_LIMIT_INITIAL_EVENT_LIMIT = 100;
const ERROR_RATE_LIMIT_SUMMARY_INTERVAL = 1000;
let lastErrorRateLimitPrunedAt = 0;

const errorRateLimitBuckets = new Map<
  string,
  { count: number; lastSummaryCount: number; windowStartedAt: number }
>();

export function resetRumStateForTesting() {
  rumInitialized = false;
  lastErrorRateLimitPrunedAt = 0;
  errorRateLimitBuckets.clear();
}

export function getErrorRateLimitBucketCountForTesting(): number {
  return errorRateLimitBuckets.size;
}

function sanitizeSampleRate(rate: number): number {
  if (Number.isNaN(rate) || rate < 0) return 0;
  if (rate > 100) return 100;
  return rate;
}

const FILTERED_RESOURCE_URL_PATTERNS = ['segment.io', 'segment.com'];

// Benign browser noise: the ResizeObserver "loop" errors mean the browser
// throttled observer notifications for a frame; nothing is broken and there
// is no app stack to act on.
const FILTERED_ERROR_MESSAGE_PATTERNS = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
];

// Errors thrown by code injected from browser extensions (e.g. MetaMask)
// are outside our control and pollute error tracking.
const FILTERED_ERROR_STACK_PATTERNS = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
];

function getErrorRateLimitKey(event: RumEvent): string | null {
  if (event.type !== 'error' || !event.error) {
    return null;
  }

  return JSON.stringify({
    sessionId: event.session?.id,
    viewId: event.view?.id,
    viewPathGroup: event.view?.url_path_group,
    errorType: event.error.type,
    errorSource: event.error.source,
    errorSourceType: event.error.source_type,
    errorFingerprint: event.error.fingerprint,
    errorMessage: event.error.message,
    errorStack: event.error.stack?.slice(0, 1000),
  });
}

function attachErrorRateLimitSummary(event: RumEvent, count: number) {
  if (event.type !== 'error') return;

  event.context = {
    ...event.context,
    rate_limit: {
      identical_error_count: count,
      initial_event_limit: ERROR_RATE_LIMIT_INITIAL_EVENT_LIMIT,
      summary_interval: ERROR_RATE_LIMIT_SUMMARY_INTERVAL,
      window_ms: ERROR_RATE_LIMIT_WINDOW_MS,
    },
  };
}

function pruneExpiredErrorRateLimitBuckets(now: number) {
  if (now - lastErrorRateLimitPrunedAt < ERROR_RATE_LIMIT_WINDOW_MS) {
    return;
  }

  lastErrorRateLimitPrunedAt = now;
  for (const [bucketKey, bucket] of errorRateLimitBuckets) {
    if (now - bucket.windowStartedAt > ERROR_RATE_LIMIT_WINDOW_MS) {
      errorRateLimitBuckets.delete(bucketKey);
    }
  }
}

function shouldDropErrorRateLimitedEvent(event: RumEvent): boolean {
  const bucketKey = getErrorRateLimitKey(event);
  if (!bucketKey) {
    return false;
  }

  const now = Date.now();
  pruneExpiredErrorRateLimitBuckets(now);
  const bucket = errorRateLimitBuckets.get(bucketKey);

  if (!bucket || now - bucket.windowStartedAt > ERROR_RATE_LIMIT_WINDOW_MS) {
    errorRateLimitBuckets.set(bucketKey, {
      count: 1,
      lastSummaryCount: ERROR_RATE_LIMIT_INITIAL_EVENT_LIMIT,
      windowStartedAt: now,
    });
    return false;
  }

  bucket.count += 1;

  if (bucket.count <= ERROR_RATE_LIMIT_INITIAL_EVENT_LIMIT) {
    return false;
  }

  if (
    bucket.count - bucket.lastSummaryCount >=
    ERROR_RATE_LIMIT_SUMMARY_INTERVAL
  ) {
    bucket.lastSummaryCount = bucket.count;
    attachErrorRateLimitSummary(event, bucket.count);
    return false;
  }

  return true;
}

export function beforeSend(event: RumEvent): boolean {
  if (event.type === 'resource') {
    const url = event.resource.url;
    if (
      FILTERED_RESOURCE_URL_PATTERNS.some((pattern) => url.includes(pattern))
    ) {
      return false;
    }
  }
  if (event.type === 'error') {
    const message = event.error?.message ?? '';
    const stack = event.error?.stack ?? '';
    if (
      FILTERED_ERROR_MESSAGE_PATTERNS.some((pattern) =>
        message.includes(pattern),
      ) ||
      FILTERED_ERROR_STACK_PATTERNS.some((pattern) =>
        stack.includes(pattern),
      ) ||
      shouldDropErrorRateLimitedEvent(event)
    ) {
      return false;
    }
  }
  return true;
}

export interface DatadogConfig {
  applicationId: string;
  clientToken: string;
  enabled: boolean;
  env: string;
  service: string;
  site: string;
  version: string;
  sessionSampleRate: number;
  traceSampleRate: number;
  allowedTracingUrls: string[];
}

interface DatadogContextType {
  isInitialized: boolean;
}

const DatadogContext = createContext<DatadogContextType>({
  isInitialized: false,
});

export const useDatadog = () => useContext(DatadogContext);

export function DatadogProvider({
  children,
  config,
}: {
  children: ReactNode;
  config: DatadogConfig;
}) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!config.applicationId || !config.clientToken) {
      console.error('Datadog configuration is missing required fields');
      return;
    }

    // Only initialize if not already initialized
    if (!isInitialized && config.enabled && typeof window !== 'undefined') {
      if (rumInitialized) {
        setIsInitialized(true);
        return;
      }

      try {
        datadogRum.init({
          applicationId: config.applicationId,
          clientToken: config.clientToken,
          site: config.site,
          service: config.service,
          env: config.env,
          version: config.version,
          sessionSampleRate: sanitizeSampleRate(config.sessionSampleRate),
          sessionReplaySampleRate: 0, // Session Replay disabled — set > 0 to enable
          traceSampleRate: sanitizeSampleRate(config.traceSampleRate),
          trackingConsent: 'not-granted',
          traceContextInjection: 'sampled',
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: 'mask-user-input',
          allowedTracingUrls: config.allowedTracingUrls,
          beforeSend,
        });

        rumInitialized = true;
        setIsInitialized(true);
        console.debug('Datadog Browser RUM initialized');
      } catch (error) {
        console.error('Failed to initialize Datadog Browser RUM:', error);
      }
    }
  }, [config, isInitialized]);

  return (
    <DatadogContext.Provider value={{ isInitialized }}>
      {children}
    </DatadogContext.Provider>
  );
}
